import React from "react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Info, AlertTriangle, Package } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export function BomView({ bom }) {
  if (!bom) return null;
  return (
    <div className="space-y-4" data-testid="bom-view">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-primary/15 to-accent/10 border border-primary/30 p-5">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-300">Estimare orientativă totală</p>
          <p className="font-display text-3xl font-extrabold text-white text-glow" data-testid="bom-total">
            {fmt(bom.estimated_total)} <span className="text-lg text-accent">{bom.currency}</span>
          </p>
        </div>
        <div className="text-right text-xs text-slate-400 font-mono">
          <div>catalog: {bom.catalog_version}</div>
          <div>reguli: {bom.rules_version}</div>
          <div>traseu: ~{bom.meters} m</div>
        </div>
      </div>

      <Accordion type="multiple" defaultValue={bom.groups?.map((g) => g.bucket)} className="space-y-3">
        {bom.groups?.map((g) => (
          <AccordionItem key={g.bucket} value={g.bucket} className="border border-slate-800 rounded-2xl bg-card overflow-hidden px-0">
            <AccordionTrigger className="px-5 hover:no-underline" data-testid={`bom-group-${g.bucket}`}>
              <div className="flex items-center justify-between w-full pr-3">
                <span className="flex items-center gap-2 font-display font-semibold text-white text-left">
                  <Package className="w-4 h-4 text-primary" /> {g.label}
                  <Badge variant="outline" className="border-slate-700 text-slate-400 ml-1">{g.items.length}</Badge>
                </span>
                <span className="font-mono text-sm text-accent whitespace-nowrap">{fmt(g.subtotal)} lei</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-4">
              <div className="space-y-2">
                {g.items.map((it, idx) => (
                  <a
                    key={idx}
                    href={it.permalink}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`bom-item-${g.bucket}-${idx}`}
                    className="flex items-center gap-3 rounded-xl bg-slate-900/60 hover:bg-slate-800 border border-slate-800 p-3 transition-colors group"
                  >
                    {it.image ? (
                      <img src={it.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-white/5 shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-slate-800 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white truncate group-hover:text-accent transition-colors">{it.name}</p>
                      <p className="text-[11px] text-slate-500 font-mono">{it.sku}</p>
                      {it.note && <p className="text-[11px] text-slate-400 mt-0.5">{it.note}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-slate-400">{it.qty} {it.unit} × {fmt(it.unit_price)}</p>
                      <p className="text-sm font-mono text-white">{fmt(it.line_total)} lei</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-600 group-hover:text-accent shrink-0" />
                  </a>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {bom.assumptions?.length > 0 && (
        <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-4">
          <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-400 mb-2">
            <Info className="w-3.5 h-3.5" /> Ipoteze de calcul
          </p>
          <ul className="text-xs text-slate-400 space-y-1 list-disc list-inside">
            {bom.assumptions.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}

      {bom.warnings?.length > 0 && (
        <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-4">
          {bom.warnings.map((w, i) => (
            <p key={i} className="flex items-center gap-1.5 text-xs text-amber-400">
              <AlertTriangle className="w-3.5 h-3.5" /> {w}
            </p>
          ))}
        </div>
      )}

      <p className="text-[11px] text-slate-500 italic flex items-start gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {bom.disclaimer}
      </p>
    </div>
  );
}
